import { noop } from '@sgme/fp';
import type { Asset } from 'apiConnector/underlying-universe';
import { createContext, type ReactNode, RefObject, useCallback, useMemo, useRef, useState } from 'react';

interface ISearchContext {
  searchFilter: string;
  setSearchFilter(searchFilter?: string): void;
  exportGridRef: RefObject<((fileName: string) => void) | null>;
  exportCsv: ((fileName: string) => void) | null;
  setExportCsv: (callback: (filename: string) => void) => void;
}

export const UnderlyingUniverseSearchContext = createContext<ISearchContext>({
  searchFilter: '',
  setSearchFilter: noop,
  exportGridRef: { current: null },
  exportCsv: null,
  setExportCsv: noop,
});

interface UnderlyingUniverseSearchProps {
  asset: Asset;
  children: ReactNode;
}

type SearchFilters = {
  [asset in Asset]?: string;
};

const emptySearchFilter = '';

export function WithUnderlyingUniverseSearchContext({ asset, children }: UnderlyingUniverseSearchProps) {
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const exportGridRef = useRef<((fileName: string) => void) | null>(null);
  const [exportCsv, setExportCsv] = useState<((fileName: string) => void) | null>(null);
  const setSearchFilter = useCallback(
    (filter = '') => {
      setSearchFilters(filters => ({ ...filters, [asset]: filter }));
    },
    [asset],
  );
  const searchFilter = searchFilters[asset] ?? emptySearchFilter;
  const context = useMemo(
    () => ({ searchFilter, setSearchFilter, exportGridRef, exportCsv, setExportCsv }),
    [searchFilter, setSearchFilter, exportCsv, setExportCsv],
  );

  return (
    <UnderlyingUniverseSearchContext.Provider value={context}>{children}</UnderlyingUniverseSearchContext.Provider>
  );
}
